import type { Method } from 'alova';
import type { RequestOptions, Response } from './type.ts'; // 复用您的 type.ts
import AdapterUniapp from '@alova/adapter-uniapp';
import { createAlova } from 'alova';
import { ContentTypeEnum, ShowMessage } from '@/enums/httpEnum';
// import { useUserStore } from '@/store/modules/user' // 假设您的 store 路径一致
import { useAppStore, useSiteStore, useTokenStore } from '@/store';
import { msg } from '@/utils/modal';
import { Storage } from '@/utils/storage';
import { uniqueSlash } from '@/utils/url-utils';
import { getApiEncrypt } from './encrypt';
import { RESPONSE_AES_SECRET } from './encrypt-config';

const UNKNOWN_ERROR: string = '未知错误，请重试';
/** mock请求路径前缀 */
const baseMockUrl: string = import.meta.env.VITE_SERVER_BASEURL;

export const alovaInstance = createAlova({
  baseURL: import.meta.env.VITE_BASE_API,
  timeout: 60000,
  ...AdapterUniapp(), // 自动适配 App/小程序/H5

  // --- 对应 request 拦截器 ---
  beforeRequest(method: Method) {
    const appStore = useAppStore();
    method.config.headers = {
      'ContentType': ContentTypeEnum.JSON,
      'Accept': 'application/json, text/plain, */*',
      'app-type': appStore.appType,
      'web-url': appStore.getHost,
      'app-version': appStore.version,
      'Custom-Referer': appStore?.pcUrl || '',
      't': Date.now(),
      'client-ip': Storage.get('ip') || '',
      'device-id': Storage.get('fingerprint') || '',
      ...method.config.headers,
    };

    if (!method.config?.headers?.site) {
      const siteStore = useSiteStore();
      method.config.headers.site = siteStore?.code || '';
    }

    const tokenStore = useTokenStore();
    // 注入 Token
    method.config.headers.Token = tokenStore.updateNowTime().validToken;
    // 注入版本信息
    // method.config.headers.LastBuildTime = __UNI_APP_VERSION__
    // method.config.headers.Version = __UNI_APP_VERSION__
  },

  // --- 对应 response 拦截器 ---
  responded: {
    onSuccess: async (response, method) => {
      // --- 对应 response 拦截器 (成功) ---
      const { config, url } = method;
      const { requestType } = config;
      const {
        statusCode,
        data,
        errMsg,
      } = response as UniNamespace.RequestSuccessCallbackResult;

      // 处理 HTTP 状态码错误
      if (statusCode !== 200) {
        const errorMessage = ShowMessage(statusCode) || `HTTP请求错误[${statusCode}]`;
        throw new Error(`${errorMessage}：${errMsg}`);
      }

      const res = data as Response;

      // 响应解密：服务端返回 data.data.encryptedData 时，用约定 AES 密钥解密为JSON 对象
      try {
        const innerData: any = (res as any)?.data;
        if (innerData && typeof innerData === 'object' && innerData.encryptedData) {
          const decryptedJson = getApiEncrypt().aesDecrypt(innerData.encryptedData, RESPONSE_AES_SECRET);
          if (decryptedJson) {
            (res as any).data = JSON.parse(decryptedJson);
          }
        }
      }
      catch (decryptErr) {
        console.error('[request] 响应解密失败:', decryptErr);
      }

      if (res && res.status === 1) {
        return res;
      }
      else if (res && res.code === 10000) {
        return res;
      }
      else if (res && res.code === 200) {
        return res;
      }
      else if (res.status === 0 && res.code === -1) {
        // 登录异常，需重新登录
        if (url.endsWith('users/logout')) {
          // 主动退出场景：由调用方处理导航，这里只清除本地 token
          await useTokenStore().logout();
        }
        else {
          // 服务端返回登录失效：统一处理导航
          useTokenStore().handleTokenExpired(res.msg || '登录已失效，请重新登录');
        }
        return Promise.reject(res);
      }
      else {
        res.message = `【错误信息：${res.msg || UNKNOWN_ERROR}】`;
        return Promise.reject(res);
      }
    },
    onError: (err, method) => {
      // --- 对应 response 拦截器 (网络/系统错误) ---
      err.msg = err?.message ?? UNKNOWN_ERROR;
      return Promise.reject(err);
    },
  },
});

// 2. 导出请求方法 (保持与 Vue3 项目调用一致)
export async function request<T = any>(config: any, options: RequestOptions = {}): Promise<T> {
  const {
    successMsg,
    permCode,
    isMock,
    isGetDataDirectly = true,
    cacheTime = 3,
    notificationType = 'error',
    encrypt = false,
  } = options;

  // 请求加密：优先使用 options.encrypt；同时兼容 vue2 风格在 data 上携带 encrypt 标记
  let needEncrypt: boolean = !!encrypt;
  if (config.data && typeof config.data === 'object' && config.data.encrypt) {
    needEncrypt = true;
    delete config.data.encrypt;
  }
  if (needEncrypt) {
    try {
      config.data = getApiEncrypt().generateFrontendData(config.data || {});
    }
    catch (e) {
      console.error('[request] 请求参数加密失败:', e);
      return Promise.reject(e);
    }
  }

  // 构建 Method 对象
  const methodConfig: any = {
    url: config.url,
    method: config.method.toUpperCase() || 'GET',
    data: config.data,
    headers: config.headers,
  };
  if (methodConfig.method === 'GET') {
    methodConfig.params = config.params || config.data;
  }

  // 处理 Mock
  if (isMock) {
    // 这里可以配置 mock 适配器拦截特定 URL，或者简单地在 baseURL 上切换
    // alova 的 mock 适配器通常需要在创建实例时配置规则，此处简化处理
    // 实际项目中建议用 @alova/mock 的规则匹配
    methodConfig.baseURL = import.meta.env.VITE_MOCK_API;
  }

  if (config.url?.startsWith('http')) {
    config.url = uniqueSlash(config.url);
  }
  else {
    const { apiUrl } = useAppStore();
    const fullUrl: string = `${(isMock ? baseMockUrl : apiUrl) + config.url}`;
    config.url = uniqueSlash(fullUrl);
  }

  // 处理缓存逻辑 (alova 内置强大缓存)
  // 如果您的逻辑是 "3秒内相同请求直接返回"，alova 的 cacheFor 完美契合
  const hasCache = options?.cache ?? false;
  if (hasCache) {
    // 设置缓存时间 (毫秒)
    // 注意：alova 默认是强制缓存，如果需要 "短时间防抖"，可配合 abort 策略或自定义
    // 这里简单映射您的 cacheTime (秒) -> 毫秒
    methodConfig.cacheFor = cacheTime * 1000;
  }
  else {
    methodConfig.cacheFor = 0;
  }
  let method: Method;
  if (config.method?.toUpperCase() === 'POST') {
    method = alovaInstance.Post(config.url, config.data, methodConfig);
  }
  else if (config.method?.toUpperCase() === 'PUT') {
    method = alovaInstance.Put(config.url, config.data, methodConfig);
  }
  else if (config.method?.toUpperCase() === 'DELETE') {
    method = alovaInstance.Delete(config.url, methodConfig);
  }
  else {
    method = alovaInstance.Get(config.url, methodConfig);
  }

  // 发送请求
  const promise: Promise<T> = method.send().then((res: any) => {
    ['all', 'success'].includes(notificationType) && msg(successMsg || res.msg, 'success');
    return isGetDataDirectly ? res?.data ?? {} : res;
  }).catch((err: any) => {
    ['error', 'all'].includes(notificationType) && msg(err.msg || UNKNOWN_ERROR, 'error');
    return Promise.reject(err);
  });
  return promise;
}

export default request;
